'use client';

// module imports
import Image from 'next/image';
import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// file imports
import { showError, showSuccess } from '@/lib/toast';
import { uploadProfilePhoto } from '@/redux/slices/uploadSlice';
import type { AppDispatch, RootState } from '@/redux/store';
interface ProfilePhotoUploadProps {
  currentPhoto?: string;
  onPhotoUpdate: (url: string) => void;
  size?: number;
  className?: string;
  disabled?: boolean;
}

export default function ProfilePhotoUpload({ currentPhoto, onPhotoUpdate, size = 200, className = '', disabled = false }: ProfilePhotoUploadProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { loading: isUploading } = useSelector((state: RootState) => state.upload);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        showError('Please select an image file');
        return;
      }

      // Validate file size (10MB max for profile photos)
      if (file.size > 10 * 1024 * 1024) {
        showError('Image size must be less than 10MB');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      try {
        const result = await dispatch(uploadProfilePhoto(file)).unwrap();

        if (result) {
          showSuccess('Profile photo updated successfully!');
          onPhotoUpdate(result.url);
          setPreviewUrl(null); // Clear preview
        }
      } catch (error: any) {
        const errorMessage = error || 'Upload failed';
        showError(errorMessage);
        setPreviewUrl(null); // Clear preview on error
      } finally {
        // Reset input value
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [dispatch, onPhotoUpdate]
  );

  const handleClick = useCallback(() => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  }, [disabled, isUploading]);

  const displayPhoto = previewUrl || currentPhoto || 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_640.png';

  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      <button
        className={`
          rounded-full overflow-hidden border-4 border-white shadow-lg 
          hover:ring-2 hover:ring-[#e92773] focus:outline-none transition
          ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        style={{ width: size, height: size }}
        onClick={handleClick}
        disabled={disabled || isUploading}
        type="button"
        title={isUploading ? 'Uploading...' : 'Click to change profile image'}
      >
        <Image src={displayPhoto} alt="Profile" width={size} height={size} className="object-cover w-full h-full" priority />

        {isUploading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="text-white text-sm">Uploading...</div>
          </div>
        )}
      </button>

      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" disabled={disabled || isUploading} />

      <button className="text-pink-500 text-xs mt-2 hover:text-pink-600 transition-colors cursor-pointer" onClick={handleClick} disabled={disabled || isUploading} type="button">
        {isUploading ? 'Uploading...' : 'Click to change profile image'}
      </button>
    </div>
  );
}
